import CodeBlock from "../components/CodeBlock";
import FAQItems from "../components/FAQItems";
import ScreenshotFrame from "../../components/ScreenshotFrame";

// Every factual claim maps to docs/how-to-check-chatgpt-ads-pixel-is-working-source-map-2026-10-13.md.

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const CARD_BORDER = "rgba(255,255,255,0.04)";
const CHECKER = "/tools/chatgpt-ads-pixel-checker";
const GUIDE = "/blog/how-to-install-chatgpt-ads-pixel-in-google-tag-manager";

function H2({ id, children }) {
  return (
    <h2
      id={id}
      style={{
        fontFamily: "var(--display)",
        fontWeight: 700,
        fontSize: "clamp(22px, 3vw, 28px)",
        color: TEXT,
        margin: "48px 0 16px",
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
      }}
    >
      {children}
    </h2>
  );
}

function H3({ children }) {
  return (
    <h3
      style={{
        fontFamily: "var(--display)",
        fontWeight: 600,
        fontSize: 19,
        color: TEXT,
        margin: "28px 0 12px",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p style={{ fontSize: 16, lineHeight: 1.75, color: TEXT_DIM, margin: "0 0 18px" }}>
      {children}
    </p>
  );
}

function Li({ children }) {
  return (
    <li style={{ fontSize: 16, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 10 }}>
      {children}
    </li>
  );
}

function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 600 }}>{children}</strong>;
}

function A({ href, children }) {
  return (
    <a
      href={href}
      style={{ color: TEAL, textDecoration: "none", borderBottom: `1px solid ${TEAL}44` }}
      {...(href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {children}
    </a>
  );
}

function Code({ children }) {
  return (
    <code
      style={{
        background: "rgba(255,255,255,0.06)",
        padding: "2px 6px",
        borderRadius: 4,
        fontFamily: "var(--mono)",
        fontSize: 13,
        color: TEXT,
      }}
    >
      {children}
    </code>
  );
}

function PullQuote({ children }) {
  return (
    <blockquote
      style={{
        margin: "32px 0",
        padding: "20px 24px",
        borderLeft: `3px solid ${TEAL}`,
        background: "rgba(79,205,196,0.05)",
        borderRadius: "0 12px 12px 0",
        fontFamily: "var(--display)",
        fontSize: 19,
        lineHeight: 1.5,
        color: TEXT,
        fontWeight: 500,
      }}
    >
      {children}
    </blockquote>
  );
}

function InlineCTA({ children, href, label }) {
  return (
    <div
      style={{
        margin: "36px 0",
        padding: "24px 28px",
        borderRadius: 14,
        background: "rgba(79,205,196,0.06)",
        border: "1px solid rgba(79,205,196,0.2)",
      }}
    >
      <div style={{ fontSize: 15, lineHeight: 1.7, color: TEXT_DIM, marginBottom: 14 }}>{children}</div>
      <a
        href={href}
        style={{
          display: "inline-block",
          borderRadius: 10,
          padding: "12px 28px",
          fontSize: 15,
          fontWeight: 600,
          minHeight: 44,
          background: TEAL,
          color: "#000",
          textDecoration: "none",
        }}
      >
        {label}
      </a>
    </div>
  );
}

const th = (accent) => ({
  textAlign: "left",
  padding: "12px 14px",
  color: accent ? TEAL : TEXT,
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  fontFamily: "var(--mono)",
});
const td = (strong) => ({
  padding: "13px 14px",
  color: strong ? TEXT : TEXT_DIM,
  fontWeight: strong ? 600 : 400,
  verticalAlign: "top",
});

function DataTable({ title, note, headers, rows, minWidth = 680 }) {
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflow: "hidden" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>{title}</div>
      <div style={{ fontSize: 12, color: TEXT_DIM, marginBottom: 16, fontFamily: "var(--body)" }}>{note}</div>
      <div className="blog-scroll-wrap" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", margin: "0 -4px" }}>
        <table style={{ width: "100%", minWidth, borderCollapse: "collapse", fontFamily: "var(--body)", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
              {headers.map((h, i) => (
                <th key={h} scope="col" style={th(i === 0)}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row[0]} style={{ borderBottom: i === rows.length - 1 ? "none" : "1px solid rgba(255,255,255,0.06)" }}>
                {row.map((cell, j) => (
                  <td key={j} style={td(j === 0)}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const checkRows = [
  ["1. Read the setup from outside", "2 min", "The pixel is installed, how, with which Pixel ID and events", "That anything fires"],
  ["2. Tag Manager Preview", "5 min", "Your tags fire on the right pages and actions", "That the pixel accepted the event"],
  ["3. Debug mode in the console", "5 min", "The pixel accepted or dropped each event, and why", "That OpenAI received it"],
  ["4. Event Stream", "5 min", "OpenAI received the event, with its payload", "That it counts as a conversion"],
  ["5. Conversion event settings", "5 min", "The event is set up as a conversion for your campaign", "Match quality over time"],
  ["6. Event quality", "After 7 days", "Matching information, duplicates and gaps over a full week", "Campaign performance"],
];

const symptomRows = [
  ["The checker finds no pixel", "Not installed, installed somewhere it cannot read (another tag manager, a Shopify app, your own scripts), or the Tag Manager container was never published", "Check 1, then Check 2"],
  ["Tag never fires in Preview", "The trigger does not match, often a custom event your site never pushes", "Check 2"],
  ["Tag fires, nothing in the console", "The base pixel did not load before the event tag, or consent is set to false", "Check 3"],
  ["Console says validation failed; event dropped", "The event broke one of OpenAI's rules: wrong fields for its type, a bad custom name, a decimal amount", "Check 3"],
  ["Event Stream shows only Pixel Initialization", "The pixel loaded but every event after it was dropped or never sent", "Checks 3 and 4"],
  ["Events arrive, Conversions stays at zero", "No conversion event setting selects that event, or the campaign does not use it", "Check 5"],
  ["Conversions objective will not accept your event", "It is a custom event; the objective needs a standard one", "Check 5"],
  ["Two or more Pixel IDs", "Often deliberate: one site measuring for several brands or partners. OpenAI supports it, with measure sending to every pixel and measureSingle to one", "Check 1, then Check 4 for each ID"],
  ["Conversion counts look doubled", "The same action sent twice, or by browser and server without a shared event ID", "Checks 1 and 6"],
  ["Ads Manager and GA4 disagree", "Different attribution windows, time zones and deduplication; not necessarily an error", "Neither: expected"],
];

const qualityRows = [
  ["Limited email or customer ID information", "Conversion events carry an email or your own stable customer ID, hashed as documented"],
  ["Ad click information is missing or cannot be connected", "The oppref parameter survives redirects to the page where the pixel runs"],
  ["Conversion matches need review", "Reloads, duplicate tags and retries are not sending the same action twice"],
  ["Many conversions are linked to the same ad interaction", "Distinct actions have distinct event IDs, and retries keep the original ID"],
  ["Limited event types observed", "The standard events for each stage of your journey are set up (custom events do not count here)"],
  ["Limited Pixel and Conversions API activity", "Events are actually arriving, not just installed"],
  ["Limited activity for a configured conversion goal", "The event your conversion setting selects is firing at all"],
];

const faqs = [
  {
    q: "How do I check if my ChatGPT Ads pixel is working?",
    a: "Work through six checks in order. Read the setup from outside with a free pixel checker, watch the tags fire in Tag Manager Preview, read the pixel's console log with debug set to true, confirm the event in Ads Manager's Event Stream, make sure a conversion event setting selects it, and read Event quality after a week. The first three prove the setup; the last three prove OpenAI is counting it.",
  },
  {
    q: "Why is my ChatGPT Ads pixel not firing?",
    a: "Typical causes are a trigger that never matches (often a custom event your site does not push), an event tag that runs before the base pixel has loaded, and a Tag Manager container that was edited but never published. Tag Manager's Preview mode shows which of these it is. If the tag fires but nothing reaches OpenAI, the pixel probably dropped the event; debug mode tells you why.",
  },
  {
    q: "Is there an OpenAI pixel helper?",
    a: "The OpenAI pixel helper browser extensions we found are independent tools, not made by OpenAI. They show pixel events on the page you are viewing. OpenAI's own equivalents are the debug option, which logs pixel activity to the browser console, and Event Stream in Ads Manager. Our free pixel checker needs no install and reads your whole Tag Manager setup, including events that fire on other pages.",
  },
  {
    q: "How quickly do pixel events show up in Ads Manager?",
    a: "Event Stream lists recent events while it is polling, so a test action should appear there within minutes. OpenAI's recent-events endpoint returns a sample from roughly the last 15 minutes. Event quality works on a slower clock: it refreshes daily using seven complete calendar days, so a fix takes up to a week to show fully in the score.",
  },
  {
    q: "Why does Tag Manager say the tag fired but no conversion shows up?",
    a: "Because firing and arriving are different things. OpenAI's pixel checks each event in the browser and drops any that break its rules, and Tag Manager still marks the tag as fired. In our tests, dropped events never reached Event Stream at all. If events do arrive but Conversions stays at zero, check that a conversion event setting selects that event.",
  },
  {
    q: "Can I use a custom event as a conversion?",
    a: "For reporting, yes: a conversion event setting can select a custom event, as long as the custom_event_name matches exactly. For the Conversion optimization objective, no. OpenAI's Help Center says custom conversion events are not currently supported there, so the event you optimize toward must be a standard one such as lead_created, registration_completed or order_created.",
  },
  {
    q: "Should I turn off debug mode after testing?",
    a: "OpenAI describes debug mode as a way to log pixel activity to the console while you test, and its documentation does not require removing it. Left on, it logs the pixel's activity in every visitor's browser console. If you do not want that, turn it off once testing is done and back on when something needs investigating.",
  },
];

const howToSteps = [
  ["Read the setup from outside", "Run the page your ads send people to through a free pixel checker to confirm the pixel is installed and see the Pixel ID, install method and events set up."],
  ["Watch the tags fire in Tag Manager Preview", "Open Preview mode, load the landing page, complete the conversion action, and confirm the pixel tags fire on the right events."],
  ["Read the pixel's log with debug mode", "Set debug to true in the pixel's init call and check the browser console for accepted events and any validation failed; event dropped messages."],
  ["Confirm OpenAI received the event", "In Ads Manager, open Event Stream for your pixel, start polling, repeat the action, and confirm the event and its payload arrive."],
  ["Make sure the event counts as a conversion", "Create or check the conversion event setting that selects this event from this pixel, and attach it to the campaign."],
  ["Read Event quality after a week", "Review the Event quality score and warnings for the pixel once seven full days of events have arrived."],
];

function Content() {
  return (
    <article>
      {/* ── Overview box ── */}
      <div
        style={{
          margin: "0 0 32px",
          padding: "20px 24px",
          borderRadius: 12,
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(255,255,255,0.07)",
        }}
      >
        <P>
          <Strong>
            To check that your ChatGPT Ads pixel is working, prove three things in order: the pixel is installed correctly, it sends events that pass OpenAI&#39;s rules, and OpenAI receives them and counts them as conversions. A tag that fires in Tag Manager proves only the first.
          </Strong>
        </P>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <Li><Strong>Fastest first look:</Strong> a free pixel checker reads your page and Tag Manager container in seconds</Li>
          <Li><Strong>The proof:</Strong> a test action you watch arrive in Ads Manager&#39;s Event Stream</Li>
          <Li><Strong>The silent failure:</Strong> the pixel drops events that break its rules, and Tag Manager still says the tag fired</Li>
          <Li><Strong>Often missed:</Strong> an event only counts as a conversion once a conversion event setting selects it</Li>
          <Li><Strong>After a week:</Strong> Event quality flags matching and duplicate problems</Li>
        </ul>
      </div>

      <H2 id="six-checks">How do you know your ChatGPT Ads pixel is working?</H2>
      <P>
        <Strong>You know when a test conversion you triggered yourself appears in Ads Manager and is selected by a conversion event setting.</Strong> Everything before that is a step toward it. The six checks below run from cheapest to most conclusive, and each one proves something the previous one cannot.
      </P>
      <DataTable
        title="Six checks for a ChatGPT Ads pixel, in order"
        note="Times are typical for one landing page and one conversion action."
        headers={["Check", "Time", "What it proves", "What it cannot prove"]}
        rows={checkRows}
      />
      <P>
        If you have not installed the pixel yet, start with our <A href={GUIDE}>pixel installation guide</A> and come back. This post assumes the pixel is on your site, or that someone told you it is. If your ads are not getting impressions at all, that is a delivery problem rather than a tracking one; start with <A href="/blog/chatgpt-ads-no-impressions">troubleshooting zero impressions</A>.
      </P>

      <H2 id="check-the-setup">Check 1: Read your setup from the outside (2 minutes)</H2>
      <P>
        <Strong>Start by reading what your site actually serves, because that is what every visitor&#39;s browser runs.</Strong> Many installs live inside Google Tag Manager rather than the page itself, so viewing the page source often shows nothing. A published Tag Manager container, though, is a public file anyone can read.
      </P>
      <P>
        Our free <A href={CHECKER}>ChatGPT Ads pixel checker</A> reads both. Enter the page your ads point to, or your container ID if your site blocks automated visits, and it reports:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li>Whether OpenAI&#39;s pixel is installed, and how: code on the page, a Custom HTML tag, OpenAI&#39;s official template, Stape&#39;s template, or another template</Li>
        <Li>The Pixel ID each tag sends to, so you can compare it with the one on the Conversions tab in Ads Manager</Li>
        <Li>Every standard and custom event set up, including events that only fire on other pages</Li>
        <Li>Problems it can see: placeholder Pixel IDs, custom event names the pixel drops, no standard conversion event, tags with no trigger, duplicate installs, and debug mode left on</Li>
      </ul>
      <ScreenshotFrame
        src="/screenshots/tools/chatgpt-ads-pixel-checker-result.png"
        title="ChatGPT Ads Pixel Checker / Result"
        chip="Site details masked"
        alt="ChatGPT Ads Pixel Checker result for a live site: pixel found via a Custom HTML tag, a masked Pixel ID, the standard events detected and setup checks."
        caption="A real result, with the site, Pixel ID and container ID masked. Notes are information; Check and Fix items need attention."
      />
      <P>
        What it cannot do is watch an event arrive. It reads the setup, not the traffic, so a clean result means the setup looks right, not that conversions are being counted. That is what the next three checks are for.
      </P>
      <P>
        It also looks for OpenAI&#39;s JavaScript pixel specifically. If a page sends conversions with OpenAI&#39;s <A href="https://developers.openai.com/ads/image-tag">image tag</A> instead, a hidden 1&times;1 image used where JavaScript does not run, the checker will not report it. Check that request in your browser&#39;s Network panel: OpenAI says an accepted request returns a 200 status, which confirms the event was received but not that processing finished.
      </P>

      <H2 id="tag-manager-preview">Check 2: Watch the tags fire in Tag Manager Preview (5 minutes)</H2>
      <P>
        <Strong>Preview mode shows which tags fire on each event, in your own browser, before anything is published.</Strong> In Tag Manager, click <Strong>Preview</Strong>, enter your landing page, and complete the action you want to measure: submit the form, start the trial, or finish the order.
      </P>
      <P>
        In the Tag Assistant panel, select the event where the action happened and confirm your pixel tags appear under <Strong>Tags Fired</Strong>. If the conversion tag is under Tags Not Fired, open it to see which trigger condition failed. Google documents the panel in its <A href="https://support.google.com/tagmanager/answer/6107056">Preview and debug guide</A>.
      </P>
      <P>
        Two things Preview cannot tell you. It does not know whether the pixel accepted the event, and changes you test in Preview are not live until you publish the container. An edited workspace that was never published looks exactly like a broken pixel.
      </P>

      <H2 id="debug-mode">Check 3: Read the pixel&#39;s own log with debug mode (5 minutes)</H2>
      <P>
        <Strong>Debug mode makes the pixel explain itself in the browser console, including the events it refuses to send.</Strong> OpenAI&#39;s <A href="https://developers.openai.com/ads/measurement-pixel">Measurement Pixel documentation</A> describes the option as a way to log pixel activity while you test. Add it to the init call:
      </P>
      <CodeBlock lang="JavaScript" syntax="javascript">{`oaiq("init", {
  pixelId: "YOUR_PIXEL_ID",
  debug: true,
});`}</CodeBlock>
      <P>
        If you installed with a Tag Manager template, use its debug setting instead of editing code. Then open your browser&#39;s developer tools, go to the Console, reload the page and repeat the conversion action. Accepted events are logged as they are sent. A dropped event logs a line like this, followed by the reason:
      </P>
      <CodeBlock lang="Console" syntax="text">{`validation failed; event dropped`}</CodeBlock>
      <P>
        The reason names the rule the event broke. Three that we reproduced are covered <A href="#fired-but-lost">further down</A>. Fix the payload, test again, and move on only when the console shows the event sent.
      </P>

      <H2 id="event-stream">Check 4: Confirm OpenAI received the event (5 minutes)</H2>
      <P>
        <Strong>Event Stream is the first check that sees your event from OpenAI&#39;s side rather than your browser&#39;s.</Strong> In Ads Manager, open the <Strong>Conversions</Strong> tab, choose <Strong>Event Stream</Strong>, pick your pixel and press <Strong>Start polling</Strong>. Then repeat the conversion action on your site and watch for it to appear, with the payload OpenAI received.
      </P>
      <PullQuote>A tag that fired is not a conversion that arrived. The only proof is an event you triggered yourself, showing up in Event Stream.</PullQuote>
      <P>
        If you only see a <Strong>Pixel Initialization</Strong> entry and nothing after it, the pixel loaded but your events were dropped or never sent. Go back to Check 3.
      </P>
      <H3>For developers: check recent events through the API</H3>
      <P>
        OpenAI also documents a recent-events endpoint in its <A href="https://developers.openai.com/ads/conversion-tracking">conversion tracking guide</A>. It returns a sample of events from roughly the last 15 minutes and labels each with its channel: <Code>pixel_sdk</Code> for browser events, <Code>server_to_server</Code> for the Conversions API. It uses your <A href="/glossary/openai-ads-api-key">OpenAI Ads API key</A>, so run it from a server or your terminal, never from the page.
      </P>
      <CodeBlock lang="Shell" syntax="bash">{`curl -G "https://api.ads.openai.com/v1/conversions/events" \\
  -H "Authorization: Bearer \${OPENAI_ADS_API_KEY}" \\
  --data-urlencode "pid=\${PIXEL_ID}" \\
  --data-urlencode "limit=50"`}</CodeBlock>

      <H2 id="conversion-event-settings">Check 5: Make sure the event counts as a conversion (5 minutes)</H2>
      <P>
        <Strong>An event arriving at OpenAI is not yet a conversion.</Strong> OpenAI&#39;s documentation is explicit that sending an event and creating a conversion event setting are separate steps: the setting selects an event from your pixel, and the campaign uses that setting for reporting and, if you choose it, optimization.
      </P>
      <P>So for each outcome you care about, confirm three things in Ads Manager:</P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li>A conversion event setting exists for the event, pointing at the right pixel</Li>
        <Li>For a custom event, the setting&#39;s name matches your <Code>custom_event_name</Code> exactly</Li>
        <Li>The setting is attached to the campaigns you want measured</Li>
      </ul>
      <P>
        One constraint shapes which events you should send. OpenAI&#39;s Help Center says custom conversion events are not currently supported for the <A href="/glossary/cost-per-action">Conversion optimization objective</A>, so the action you want campaigns to optimize toward needs a standard event: <Code>lead_created</Code>, <Code>registration_completed</Code>, <Code>trial_started</Code>, <Code>order_created</Code> and so on. Custom events still work for reporting, and our <A href="/blog/how-to-set-up-chatgpt-ads-conversion-campaigns">Conversions campaign setup guide</A> covers which standard event B2B teams should optimize toward. Our guide to <A href="/blog/how-to-use-chatgpt-ads-manager">using ChatGPT Ads Manager</A> covers when to switch a campaign from Clicks to Conversions.
      </P>

      <InlineCTA href={CHECKER} label="Check your pixel setup free">
        The free ChatGPT Ads Pixel Checker lists every event your site is set up to send and warns when none of them is a standard conversion event, before you build a campaign around one. No sign-up, nothing to install.
      </InlineCTA>

      <H2 id="event-quality">Check 6: Read Event quality after a week</H2>
      <P>
        <Strong>Event quality is OpenAI&#39;s own audit of the events it received, and it is the only check that looks at a full week.</Strong> Each pixel or data source gets its own score and warnings, refreshed daily from seven complete calendar days of events. OpenAI is clear that the score does not predict campaign performance; it points at parts of your setup to review.
      </P>
      <P>
        That window is why this check comes last. After you fix something, new events have to arrive and old ones have to age out, so improvements show gradually. OpenAI&#39;s <A href="https://help.openai.com/en/articles/20001513-understand-and-improve-event-quality">Event quality guide</A> also warns against gaming it: do not send extra events, invent identifiers or change event times to improve a score. The warnings, and what each one asks you to check:
      </P>
      <DataTable
        title="Event quality warnings and what to check"
        note="Warning names as published in OpenAI's Help Center, October 2026."
        headers={["Warning", "Check that"]}
        rows={qualityRows}
        minWidth={560}
      />
      <P>
        Two of these connect to earlier checks. Click information depends on the <Code>oppref</Code> parameter OpenAI adds to your landing page URL, which the pixel stores in a first-party cookie, so redirects that strip query parameters break it. And matching information is where <A href="/blog/chatgpt-ads-automatic-advanced-matching">automatic advanced matching</A> helps, within the limits of your consent setup.
      </P>

      <H2 id="fired-but-lost">Why a pixel can fire and still lose conversions</H2>
      <P>
        <Strong>OpenAI&#39;s pixel validates every event in the browser and drops the ones that break its rules, without an error that Tag Manager can see.</Strong> We tested this directly in October 2026, replaying real Tag Manager template output, plus a few hand-built events, through OpenAI&#39;s live pixel and a dedicated test pixel in our own Ads Manager account. Three payloads were dropped:
      </P>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>A lead carrying product data.</Strong> Sent through Stape&#39;s Tag Manager template with its Data Layer settings at their defaults, on a page that had earlier pushed GA4 ecommerce data, a lead event picked up the product as <Code>contents</Code>. The pixel rejected it: <Code>eventProps.contents is not a documented field</Code>. Clearing ecommerce data before the lead fixed it.</Li>
        <Li><Strong>A custom event name with a dot.</Strong> The same template, set to inherit its event name and fired by Tag Manager&#39;s built-in Form Submission trigger, sent <Code>gtm.formSubmit</Code> as the event name, which breaks OpenAI&#39;s naming rules.</Li>
        <Li><Strong>A decimal amount.</Strong> An amount of 23.5 was rejected: <Code>eventProps.amount should be an integer</Code>. OpenAI expects whole numbers in the currency&#39;s minor unit, such as 8900 for $89.00.</Li>
      </ul>
      <P>
        In the two template cases, the tag reported success to Tag Manager. In Event Stream, the only thing that arrived for any of the three tests was the pixel&#39;s initialization ping. And when we checked Ads Manager&#39;s diagnostics minutes later, they showed no events at risk.
      </P>
      <PullQuote>The only check that caught the lost leads was a test action watched in Event Stream. Preview, the tag&#39;s status and the diagnostics all looked fine.</PullQuote>
      <P>
        Diagnostics may simply lag, which is one more reason to run Check 4 yourself rather than wait for a warning. If your conversion events are leads, test one on a page that also tracks products, because that is the combination that broke.
      </P>

      <H2 id="symptoms">What you see, what it means, and which check finds it</H2>
      <P>
        <Strong>Most pixel problems show up as one of a handful of symptoms.</Strong> Start from what you are seeing:
      </P>
      <DataTable
        title="ChatGPT Ads pixel symptoms and causes"
        note="Causes drawn from OpenAI's documentation and our own testing. The check column refers to the six checks above."
        headers={["You see", "Likely cause", "Check"]}
        rows={symptomRows}
      />
      <P>
        The last row is not a bug. OpenAI notes that Ads Manager, your analytics and other ad platforms can report different totals because of attribution windows, time zones, consent conditions and deduplication, and that a difference does not necessarily indicate an error. Our post on <A href="/blog/how-to-track-chatgpt-ads-in-ga4">tracking ChatGPT Ads in GA4</A> explains what to expect on the GA4 side.
      </P>
      <P>
        Before you launch, run the whole sequence once on the page each campaign points to, and repeat Checks 3 and 4 whenever someone changes your Tag Manager setup, your forms or your checkout. For the measurement strategy behind these events, see our overview of <A href="/blog/chatgpt-ads-conversion-tracking">ChatGPT Ads conversion tracking</A> and how it fits <A href="/glossary/chatgpt-ads">ChatGPT Ads</A> as a channel.
      </P>

      {/* ── HowTo + FAQPage JSON-LD ── */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to check your ChatGPT Ads pixel is working",
            step: howToSteps.map(([name, text], i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name,
              text,
            })),
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          }),
        }}
      />

      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href={GUIDE}>How to install the ChatGPT Ads Measurement Pixel via Google Tag Manager</A></Li>
        <Li><A href="/blog/chatgpt-ads-conversion-tracking">ChatGPT Ads Conversion Tracking Is Real: What OpenAI&#39;s April 30 Privacy Policy Update Tells You About the Roadmap</A></Li>
        <Li><A href="/blog/chatgpt-ads-automatic-advanced-matching">ChatGPT Ads Automatic Advanced Matching Turns On August 17</A></Li>
        <Li><A href="/blog/how-to-track-chatgpt-ads-in-ga4">How to Track ChatGPT Ads in GA4 (What Actually Shows Up, Verified)</A></Li>
        <Li><A href="/blog/how-to-use-chatgpt-ads-manager">How to Use ChatGPT Ads: A Marketer&#39;s Step-by-Step Guide (2026)</A></Li>
      </ul>
    </article>
  );
}

const post = {
  slug: "how-to-check-chatgpt-ads-pixel-is-working",
  title: "How to Check Your ChatGPT Ads Pixel Is Working",
  seoTitle: "How to Check Your ChatGPT Ads Pixel Is Working",
  excerpt:
    "A tag that fires in Tag Manager is not a conversion OpenAI counted. Six checks, from a free outside read of your setup to Event Stream and Event quality, plus the silent drops we reproduced against OpenAI's live pixel.",
  metaDescription:
    "Six checks to confirm your ChatGPT Ads pixel works: setup, Tag Manager Preview, debug mode, Event Stream, conversion settings and Event quality.",
  date: "2026-10-14",
  lastUpdated: "2026-10-17",
  readTime: "10 min read",
  category: "AI Ads",
  tags: ["ChatGPT Ads", "Measurement Pixel", "Conversion Tracking", "Google Tag Manager", "Event Stream", "Event Quality", "HowTo"],
  Content,
};

export default post;
